import { getRequest, postMultipartRequest, postRequest } from './api';

function _token() {
  try {
    return window.localStorage.getItem('access_token');
  } catch (_e) {
    return null;
  }
}

function _authHeader() {
  const token = _token();
  if (!token) return {};
  return { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` };
}

function _base() {
  return process.env.REACT_APP_API_URL || '';
}

// Bebo IA — admin conversational analyst over the warehouse (read-only).
const analystApi = {
  // Attach a CSV/Excel file to the conversation. Returns { file_id, filename,
  // columns, row_count, preview } on 201, or throws Error(message) on a rejected
  // upload (over the row/size limit, bad format) so the UI can show WHY.
  async uploadFile(file) {
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await postMultipartRequest('/admin/analyst/upload', form);
      return res?.data ?? res;
    } catch (e) {
      throw new Error(
        e?.response?.data?.error || 'No se pudo subir el archivo.'
      );
    }
  },
  async getBrands() {
    const res = await getRequest('/admin/analyst/brands');
    return res?.data ?? res;
  },
  // Creative library (ad publisher). List a brand's uploaded creatives.
  async listCreatives(brand) {
    const res = await getRequest(
      `/admin/analyst/creative-library?brand=${encodeURIComponent(brand || '')}`
    );
    return res?.data ?? res; // { assets: [...] }
  },
  // The brand's Meta campaign -> adset -> ad tree (ids + names + spend) for the
  // publisher's pickers. Returns { campaigns: [...] }.
  async getMetaStructure(brand) {
    const res = await getRequest(
      `/admin/analyst/meta/structure?brand=${encodeURIComponent(brand || '')}`
    );
    return res?.data ?? res;
  },
  // Prepare creating a new ad from a library asset using picker-selected ids.
  // Returns the same proposal shape the chat card renders.
  async prepareCreativeAd(payload) {
    const res = await postRequest('/admin/analyst/creative-ad/prepare', payload);
    return res?.data ?? res;
  },
  // Prepare cloning an ad set (deep copy + its ads). Returns the editable form.
  async prepareAdsetClone(payload) {
    const res = await postRequest('/admin/analyst/adset-clone/prepare', payload);
    return res?.data ?? res;
  },
  // Upload one or more creatives to a brand's library. Returns { results: [...] }
  // where each item is { ok, duplicate?, asset? , error? } so the UI shows per-file.
  async uploadCreatives(brand, files) {
    const form = new FormData();
    form.append('brand', brand);
    Array.from(files).forEach((f) => form.append('files', f));
    try {
      const res = await postMultipartRequest('/admin/analyst/creative-library/upload', form);
      return res?.data ?? res;
    } catch (e) {
      throw new Error(e?.response?.data?.error || 'No se pudo subir el material.');
    }
  },
  async query(question, brand) {
    const res = await postRequest('/admin/analyst/query', { question, brand });
    return res?.data ?? res;
  },
  // Proactive insights (the "hand-raise" mascot). Returns { insights, badge }.
  async getInsights(brand) {
    const res = await getRequest(
      `/admin/analyst/insights?brand=${encodeURIComponent(brand || '')}`
    );
    return res?.data ?? res;
  },
  // Mark an insight seen / snoozed / dismissed.
  async setInsightState(insightId, state) {
    const res = await postRequest(`/admin/analyst/insights/${insightId}/state`, { state });
    return res?.data ?? res;
  },
  // Apply a user-confirmed action Bebo proposed (pause / budget change).
  async applyAction({ brand, provider, action, entity, apply_payload: applyPayload }) {
    const res = await postRequest('/admin/analyst/actions/apply', {
      confirm: true,
      brand,
      provider: provider || 'meta',
      action,
      entity,
      apply_payload: applyPayload,
    });
    return res?.data ?? res;
  },
  // Apply a user-confirmed budget reallocation (account_review v2), best-effort
  // row by row. `rows` carry provider / provider_action / apply_payload.
  async applyReallocation({ brand, rows }) {
    const res = await postRequest('/admin/analyst/actions/apply-reallocation', {
      confirm: true,
      brand,
      rows,
    });
    return res?.data ?? res;
  },
  // Download the .xlsx for a reconciliation result (the reconcile_result event
  // payload). Builds the file server-side and triggers a browser download.
  async exportReconcile(result) {
    const resp = await fetch(`${_base()}/admin/analyst/reconcile/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ..._authHeader() },
      body: JSON.stringify(result || {}),
    });
    if (!resp.ok) {
      let msg = 'No se pudo generar el Excel.';
      try {
        msg = (await resp.json())?.error || msg;
      } catch (_e) {
        /* non-JSON error body */
      }
      throw new Error(msg);
    }
    const blob = await resp.blob();
    const brand = String(result?.brand || 'marca').replace(/\s+/g, '_');
    const snap = result?.snapshot_date || '';
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reconciliacion_${brand}_${snap}.xlsx`.replace(/_+/g, '_');
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Streaming (SSE) query. Bypasses axios to read the response body incrementally.
// Calls onStatus(tool) before each tool runs and onDelta(text) for each text chunk.
export async function streamQuery(
  question,
  brand,
  {
    history,
    fileIds,
    creativeIds,
    onStatus,
    onDelta,
    onError,
    onActionProposal,
    onReallocationProposal,
    onChart,
    onReconcileResult,
    signal,
  } = {}
) {
  const resp = await fetch(`${_base()}/admin/analyst/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ..._authHeader(),
    },
    body: JSON.stringify({
      question,
      brand,
      history: history || [],
      file_ids: fileIds || [],
      creative_ids: creativeIds || [],
    }),
    signal,
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`stream failed: ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let terminated = false; // the backend ends with a {type:'done'} or {type:'error'} event

  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith('data:')) continue; // skips heartbeat comment lines (": ping")
      let evt;
      try {
        evt = JSON.parse(line.slice(5).trim());
      } catch (_e) {
        continue;
      }
      if (evt.type === 'delta') onDelta?.(evt.text);
      else if (evt.type === 'status') onStatus?.(evt.tool);
      else if (evt.type === 'action_proposal') onActionProposal?.(evt.proposal);
      else if (evt.type === 'reallocation_proposal') onReallocationProposal?.(evt.proposal);
      else if (evt.type === 'chart') onChart?.(evt.spec);
      else if (evt.type === 'reconcile_result') onReconcileResult?.(evt.result);
      else if (evt.type === 'done') terminated = true;
      else if (evt.type === 'error') {
        terminated = true;
        onError?.(evt.message);
      }
    }
  }

  // The stream closed without a terminal ('done' / 'error') event: it was
  // truncated (e.g. a proxy timeout), not finished — surface it instead of
  // leaving a partial answer that looks complete. An intentional abort is not
  // an error.
  if (!terminated && !signal?.aborted) {
    onError?.('La respuesta se cortó antes de terminar. Probá de nuevo.');
  }
}

export default analystApi;
